import { motion } from "framer-motion";
import { Pause, Play, RotateCcw } from "lucide-react";
import React from "react";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Button } from "./components/ui/button";
import { toast } from "sonner";
import confetti from "canvas-confetti";

// Constants
const GRID_WIDTH = 10;
const GRID_HEIGHT = 20;
const INITIAL_FALL_SPEED = 1000;

const getRandomLetter = () => {
  const commonLetters = "ETAOINSHRDLUCMFWYPVBGKJQXZ";
  const randomValue = Math.random();
  if (randomValue < 0.7) {
    // 70% chance of getting a common letter
    return commonLetters[Math.floor(Math.random() * 12)]; // First 12 letters are most common
  } else if (randomValue < 0.95) {
    // 25% chance of getting a less common letter
    return commonLetters[
      Math.floor(Math.random() * (commonLetters.length - 12) + 12)
    ];
  } else {
    // 5% chance of getting a wild card
    return "*";
  }
};

const checkForWords = (grid, dictionary) => {
  const words = [];

  const isValidWord = (word) => {
    // Convert wildcards to regex
    const regexWord = word.replace(/\*/g, ".");
    const regex = new RegExp(`^${regexWord}$`, "i");

    // Check if the word (with potential wildcards) is in the dictionary
    return [...dictionary].some((dictWord) => regex.test(dictWord));
  };

  const processWord = (word, startPos, endPos, direction) => {
    if (word.length >= 3 && isValidWord(word)) {
      const matchedWord = [...dictionary].find((dictWord) =>
        new RegExp(`^${word.replace(/\*/g, ".")}$`, "i").test(dictWord),
      );
      words.push({
        word: matchedWord || word,
        start: startPos,
        end: endPos,
        direction: direction,
      });
    }
  };

  // Check horizontal words
  for (let y = 0; y < GRID_HEIGHT; y++) {
    let word = "";
    let startPos = null;
    for (let x = 0; x < GRID_WIDTH; x++) {
      if (grid[y][x]) {
        if (!word) startPos = [y, x];
        word += grid[y][x];
      } else if (word) {
        processWord(word, startPos, [y, x - 1], "horizontal");
        word = "";
        startPos = null;
      }
    }
    if (word) {
      processWord(word, startPos, [y, GRID_WIDTH - 1], "horizontal");
    }
  }

  // Check vertical words
  for (let x = 0; x < GRID_WIDTH; x++) {
    let word = "";
    let startPos = null;
    for (let y = 0; y < GRID_HEIGHT; y++) {
      if (grid[y][x]) {
        if (!word) startPos = [y, x];
        word += grid[y][x];
      } else if (word) {
        processWord(word, startPos, [y - 1, x], "vertical");
        word = "";
        startPos = null;
      }
    }
    if (word) {
      processWord(word, startPos, [GRID_HEIGHT - 1, x], "vertical");
    }
  }

  return words;
};

const removeWords = (grid, words) => {
  const newGrid = [...grid];
  words.forEach((w) => {
    if (w.direction === "horizontal") {
      for (let x = w.start[1]; x <= w.end[1]; x++) {
        newGrid[w.start[0]][x] = null;
      }
    } else {
      for (let y = w.start[0]; y <= w.end[0]; y++) {
        newGrid[y][w.start[1]] = null;
      }
    }
  });
  return newGrid;
};

const repeatShape = (shape, times) => Array(times).fill(shape);

// Define the shapes
const shapes = [
  [[1]], // Single letter
  [[1, 1]], // Vertical 2-letter
  [[1, 1, 1]], // Vertical 3-letter
  [
    [1, 1],
    [1, 1],
  ], // 2x2 square
  [
    [1, 1, 1],
    [0, 0, 1],
  ], // L-shape
  [
    [1, 1, 1],
    [0, 1, 0],
  ], // T-shape
  [[1, 1, 1, 1]], // Horizontal 4-letter
];

// Number of times to repeat each shape
const repeatTimes = 3;

// Repeat each shape
const repeatedShapes = shapes.flatMap((shape) =>
  repeatShape(shape, repeatTimes),
);

// Add common words and suffixes
const commonWordsAndSuffixes = [
  [["T", "H", "E"]], // Common word "THE"
  [["I", "N", "G"]], // Common suffix "ING"
  [
    ["E", "D"],
    ["*", "*"],
  ], // Common suffix "ED" with wildcards
];

// Combine all pieces
const PIECES = [...repeatedShapes, ...commonWordsAndSuffixes];

// Helper functions
const createEmptyGrid = () =>
  Array(GRID_HEIGHT)
    .fill(null)
    .map(() => Array(GRID_WIDTH).fill(null));
const randomPiece = () => {
  const shape = PIECES[Math.floor(Math.random() * PIECES.length)];
  return shape.map((row) =>
    row.map((cell) => {
      if (cell === 1) return getRandomLetter();
      if (cell === "*") return "*";
      return cell; // For predefined letters
    }),
  );
};

const INITIAL_STATE = {
  grid: createEmptyGrid(),
  currentPiece: { shape: randomPiece(), x: 3, y: 0 },
  nextPiece: randomPiece(),
  score: 0,
  level: 1,
  gameOver: false,
  isPaused: false,
};

// Custom hooks
const useGameState = () => {
  const [grid, setGrid] = React.useState(INITIAL_STATE.grid);
  const [currentPiece, setCurrentPiece] = React.useState(
    INITIAL_STATE.currentPiece,
  );
  const [nextPiece, setNextPiece] = React.useState(INITIAL_STATE.nextPiece);
  const [score, setScore] = React.useState(INITIAL_STATE.score);
  const [level, setLevel] = React.useState(INITIAL_STATE.level);
  const [gameOver, setGameOver] = React.useState(INITIAL_STATE.gameOver);
  const [isPaused, setIsPaused] = React.useState(INITIAL_STATE.isPaused);
  const [highlightedWords, setHighlightedWords] = React.useState([]);

  return {
    grid,
    setGrid,
    currentPiece,
    setCurrentPiece,
    nextPiece,
    setNextPiece,
    score,
    setScore,
    level,
    setLevel,
    gameOver,
    setGameOver,
    isPaused,
    setIsPaused,
    highlightedWords,
    setHighlightedWords,
  };
};

const useGameLogic = (
  gameState,
  dictionary,
  setHighlightedWords,
  handleWordFormation,
) => {
  const {
    grid,
    setGrid,
    currentPiece,
    setCurrentPiece,
    nextPiece,
    setNextPiece,
    score,
    setScore,
    level,
    setLevel,
    setGameOver,
  } = gameState;

  const shiftLettersDown = React.useCallback((grid) => {
    const newGrid = grid.map((row) => [...row]);
    for (let col = 0; col < GRID_WIDTH; col++) {
      let emptyRow = GRID_HEIGHT - 1;
      for (let row = GRID_HEIGHT - 1; row >= 0; row--) {
        if (newGrid[row][col] !== null) {
          if (row !== emptyRow) {
            newGrid[emptyRow][col] = newGrid[row][col];
            newGrid[row][col] = null;
          }
          emptyRow--;
        }
      }
    }
    return newGrid;
  }, []);

  const canMoveTo = React.useCallback(
    (shape, x, y) => {
      for (let row = 0; row < shape.length; row++) {
        for (let col = 0; col < shape[row].length; col++) {
          if (shape[row][col]) {
            if (
              x + col < 0 ||
              x + col >= GRID_WIDTH ||
              y + row >= GRID_HEIGHT ||
              (y + row >= 0 && grid[y + row] && grid[y + row][x + col] !== null)
            ) {
              return false;
            }
          }
        }
      }
      return true;
    },
    [grid],
  );

  const placePiece = React.useCallback(() => {
    let newGrid = [...grid];
    currentPiece.shape.forEach((row, y) => {
      row.forEach((cell, x) => {
        if (cell && currentPiece.y + y >= 0) {
          newGrid[currentPiece.y + y][currentPiece.x + x] = cell;
        }
      });
    });

    let linesCleared = 0;
    newGrid = newGrid.filter((row) => {
      if (row.every((cell) => cell !== null)) {
        linesCleared++;
        return false;
      }
      return true;
    });

    while (newGrid.length < GRID_HEIGHT) {
      newGrid.unshift(Array(GRID_WIDTH).fill(null));
    }

    const words = checkForWords(newGrid, dictionary);
    const validWords = words.filter((w) => {
      const processedWord = w.word.replace(/\*/g, ".");
      const regex = new RegExp(`^${processedWord}$`);
      return [...dictionary].some((dictWord) => regex.test(dictWord));
    });

    if (validWords.length > 0) {
      handleWordFormation(validWords);
      newGrid = removeWords(newGrid, validWords);
      newGrid = shiftLettersDown(newGrid);
    }

    setGrid(newGrid);
    spawnNewPiece();

    setTimeout(() => {
      setHighlightedWords([]);
    }, 500);
  }, [
    currentPiece,
    grid,
    setGrid,
    dictionary,
    handleWordFormation,
    shiftLettersDown,
    spawnNewPiece,
    setHighlightedWords,
  ]);

  const spawnNewPiece = React.useCallback(() => {
    const newPiece = { shape: nextPiece, x: 3, y: 0 };
    if (!canMoveTo(newPiece.shape, newPiece.x, newPiece.y)) {
      setGameOver(true);
      // Toast for game over
      toast("Game Over!", {
        description: `Final score: ${score}`,
        variant: "destructive",
      });
    } else {
      setCurrentPiece(newPiece);
      setNextPiece(randomPiece());
    }
  }, [
    nextPiece,
    canMoveTo,
    setCurrentPiece,
    setNextPiece,
    setGameOver,
    score,
    toast,
  ]);
  const moveDown = React.useCallback(() => {
    if (canMoveTo(currentPiece.shape, currentPiece.x, currentPiece.y + 1)) {
      setCurrentPiece((prev) => ({ ...prev, y: prev.y + 1 }));
    } else {
      placePiece();
    }
  }, [currentPiece, canMoveTo, setCurrentPiece, placePiece]);

  const moveLeft = () => {
    if (canMoveTo(currentPiece.shape, currentPiece.x - 1, currentPiece.y)) {
      setCurrentPiece((prev) => ({ ...prev, x: prev.x - 1 }));
    }
  };

  const moveRight = () => {
    if (canMoveTo(currentPiece.shape, currentPiece.x + 1, currentPiece.y)) {
      setCurrentPiece((prev) => ({ ...prev, x: prev.x + 1 }));
    }
  };

  const rotatePiece = () => {
    const rotated = currentPiece.shape[0].map((_, index) =>
      currentPiece.shape.map((row) => row[index]).reverse(),
    );
    if (canMoveTo(rotated, currentPiece.x, currentPiece.y)) {
      setCurrentPiece((prev) => ({ ...prev, shape: rotated }));
    }
  };

  return { moveDown, moveLeft, moveRight, rotatePiece };
};

// Components
const Cell = ({ value, isCurrent, isHighlighted, isNextPiece }) => (
  <motion.div
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 0.2 }}
    whileHover={{ scale: 1.1 }}
    className={`
      w-full aspect-square
      flex items-center justify-center text-xs sm:text-sm md:text-base font-bold rounded-sm
      ${value === "*" ? "bg-yellow-400 text-black" : ""}
      ${value && value !== "*" && isNextPiece ? "bg-indigo-500 text-white" : ""}
      ${
        value && value !== "*" && !isNextPiece
          ? isCurrent
            ? "bg-cyan-500 text-white"
            : "bg-indigo-500 text-white"
          : "bg-gray-700"
      }
      ${isCurrent && !isNextPiece ? "ring-2 ring-cyan-300" : ""}
      ${isHighlighted ? "bg-green-400 text-black" : ""}
      transition-all duration-200
    `}
  >
    {value === "*" ? "★" : value || ""}
  </motion.div>
);

const Grid = ({ grid, currentPiece, highlightedWords }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="grid grid-cols-10 gap-px bg-gray-800 p-1 rounded-lg shadow-lg w-full max-w-md mx-auto"
  >
    {grid.map((row, y) =>
      row.map((cell, x) => (
        <Cell
          key={`${x}-${y}`}
          value={
            cell ||
            currentPiece.shape[y - currentPiece.y]?.[x - currentPiece.x] ||
            null
          }
          isCurrent={
            currentPiece.shape[y - currentPiece.y]?.[x - currentPiece.x]
          }
          isHighlighted={highlightedWords.some(
            (w) =>
              (w.direction === "horizontal" &&
                y === w.start[0] &&
                x >= w.start[1] &&
                x <= w.end[1]) ||
              (w.direction === "vertical" &&
                x === w.start[1] &&
                y >= w.start[0] &&
                y <= w.end[0]),
          )}
          isNextPiece={false}
        />
      )),
    )}
  </motion.div>
);

const NextPiece = ({ piece }) => {
  const maxWidth = Math.max(...piece.map((row) => row.length));
  const maxHeight = piece.length;

  return (
    <div className="bg-gray-800 p-2 sm:p-4 rounded-lg shadow-lg">
      <h2 className="text-base sm:text-lg font-semibold mb-2 text-indigo-300">
        Next:
      </h2>
      <div
        className={`grid gap-1 bg-gray-700 p-2 rounded justify-center items-center`}
        style={{ gridTemplateColumns: `repeat(${maxWidth}, minmax(0, 1fr))` }}
      >
        {piece.map((row, y) => (
          <React.Fragment key={y}>
            {row.map((cell, x) => (
              <Cell
                key={`next-${x}-${y}`}
                value={cell}
                isCurrent={false}
                isNextPiece={true}
              />
            ))}
            {[...Array(maxWidth - row.length)].map((_, i) => (
              <div key={`empty-${y}-${i}`} className="w-5 h-5 sm:w-6 sm:h-6" />
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const Controls = ({ onMoveLeft, onMoveRight, onMoveDown, onRotate }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.2 }}
    className="grid grid-cols-3 gap-2 mt-4"
  >
    <Button
      onClick={onMoveLeft}
      className="bg-indigo-600 hover:bg-indigo-700 text-white transition-colors duration-200"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      ←
    </Button>
    <Button
      onClick={onMoveDown}
      className="bg-indigo-600 hover:bg-indigo-700 text-white transition-colors duration-200"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      ↓
    </Button>
    <Button
      onClick={onMoveRight}
      className="bg-indigo-600 hover:bg-indigo-700 text-white transition-colors duration-200"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      →
    </Button>
    <Button
      onClick={onRotate}
      className="bg-indigo-600 hover:bg-indigo-700 text-white col-span-3 transition-colors duration-200"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      Rotate ↻
    </Button>
  </motion.div>
);

const ScoreBoard = ({ score, level }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.5 }}
    className="bg-gray-800 p-2 sm:p-4 rounded-lg shadow-lg text-center"
  >
    <div className="text-xl sm:text-2xl font-bold text-indigo-300 mb-2">
      Score
    </div>
    <motion.div
      key={score}
      initial={{ scale: 1.5, color: "#ffffff" }}
      animate={{ scale: 1, color: "#ffffff" }}
      transition={{ duration: 0.3 }}
      className="text-2xl sm:text-3xl font-bold mb-4"
    >
      {score}
    </motion.div>
    <div className="text-lg sm:text-xl font-semibold text-indigo-300 mb-2">
      Level
    </div>
    <motion.div
      key={level}
      initial={{ scale: 1.5, color: "#ffffff" }}
      animate={{ scale: 1, color: "#ffffff" }}
      transition={{ duration: 0.3 }}
      className="text-xl sm:text-2xl font-bold"
    >
      {level}
    </motion.div>
  </motion.div>
);

const WordTetris = () => {
  const [dictionary, setDictionary] = React.useState(new Set());
  const [isLoading, setIsLoading] = React.useState(true);

  const gameState = useGameState();
  const {
    grid,
    setGrid,
    currentPiece,
    setCurrentPiece,
    nextPiece,
    setNextPiece,
    score,
    setScore,
    level,
    setLevel,
    gameOver,
    setGameOver,
    isPaused,
    setIsPaused,
    highlightedWords,
    setHighlightedWords,
  } = gameState;

  React.useEffect(() => {
    const loadDictionary = async () => {
      try {
        const response = await fetch("/words_alpha.txt");
        console.log(response);
        if (!response.ok) {
          throw new Error("Failed to load dictionary");
        }
        const text = await response.text();
        console.log(text);
        const words = text.split("\n").map((word) => word.trim().toUpperCase());
        setDictionary(new Set(words));
      } catch (error) {
        console.error("Error loading dictionary:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDictionary();
  }, []);

  const resetGame = () => {
    setGrid(INITIAL_STATE.grid);
    setCurrentPiece(INITIAL_STATE.currentPiece);
    setNextPiece(INITIAL_STATE.nextPiece);
    setScore(INITIAL_STATE.score);
    setLevel(INITIAL_STATE.level);
    setGameOver(INITIAL_STATE.gameOver);
    setIsPaused(INITIAL_STATE.isPaused);
  };

  const handleWordFormation = React.useCallback(
    (words) => {
      setScore((prev) => prev + words.length * 100 * level);
      setHighlightedWords(words);

      // Trigger confetti effect
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });

      // Toast for word formation
      toast("Word Formed!", {
        description: `You formed ${words.length} word${words.length > 1 ? "s" : ""}! +${words.length * 100 * level} points. ${words.map((word) => word.word)}`,
        style: {
          background: "linear-gradient(to right, #4f46e5, #4f46e5 50%)",
          color: "white",
        },
      });

      // Check for level up
      const newScore = score + words.length * 100 * level;
      const newLevel = Math.floor(newScore / 1000) + 1;
      if (newLevel > level) {
        setLevel(newLevel);
        toast("Level Up!", {
          description: `You've reached level ${newLevel}!`,
          style: {
            background: "linear-gradient(to right, #10b981, #34d399)",
            color: "white",
          },
        });
      }
    },
    [level, score, setScore, setHighlightedWords, setLevel],
  );

  const { moveDown, moveLeft, moveRight, rotatePiece } = useGameLogic(
    gameState,
    dictionary,
    setHighlightedWords,
    handleWordFormation,
  );

  React.useEffect(() => {
    if (gameOver || isPaused) return;
    const intervalId = setInterval(moveDown, INITIAL_FALL_SPEED / level);
    return () => clearInterval(intervalId);
  }, [gameOver, isPaused, level, moveDown]);

  React.useEffect(() => {
    const handleKeyPress = (event) => {
      if (gameOver || isPaused) return;
      switch (event.key) {
        case "ArrowLeft":
          moveLeft();
          break;
        case "ArrowRight":
          moveRight();
          break;
        case "ArrowDown":
          moveDown();
          break;
        case "ArrowUp":
          rotatePiece();
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [gameOver, isPaused, moveDown, moveLeft, moveRight, rotatePiece]);

  const togglePause = () => setIsPaused(!isPaused);

  if (isLoading) {
    return <div>Loading dictionary...</div>;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 p-2 sm:p-4">
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-gray-800 rounded-xl shadow-2xl p-4 sm:p-6 mb-4 w-full max-w-4xl"
      >
        <h1 className="text-3xl sm:text-4xl font-bold text-center mb-4 sm:mb-6 text-indigo-400">
          Word Tetris
        </h1>
        <div className="flex flex-col md:flex-row gap-4 md:gap-6">
          <div className="flex-1">
            <Grid
              grid={grid}
              currentPiece={currentPiece}
              highlightedWords={highlightedWords}
            />
            <Controls
              onMoveLeft={moveLeft}
              onMoveRight={moveRight}
              onMoveDown={moveDown}
              onRotate={rotatePiece}
            />
          </div>
          <div className="flex flex-row md:flex-col justify-between gap-4">
            <ScoreBoard score={score} level={level} />
            <NextPiece piece={nextPiece} />
            <div className="flex gap-2 mt-4">
              <Button
                onClick={togglePause}
                className="bg-cyan-600 hover:bg-cyan-700 text-white px-3 sm:px-4 py-2"
              >
                {isPaused ? (
                  <Play className="mr-1 sm:mr-2" />
                ) : (
                  <Pause className="mr-1 sm:mr-2" />
                )}
                {isPaused ? "Resume" : "Pause"}
              </Button>
              <Button
                onClick={resetGame}
                className="bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-2"
              >
                <RotateCcw className="mr-1 sm:mr-2" />
                Reset
              </Button>
            </div>
            {(gameOver || isPaused) && (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Alert className="mt-4 w-64 sm:w-80 bg-gray-700 border-indigo-500 text-white">
                  <AlertDescription>
                    {gameOver
                      ? `Game Over! Final score: ${score}`
                      : "Game Paused"}
                  </AlertDescription>
                </Alert>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default WordTetris;
